import React, { useState, useEffect } from 'react';
import './PokemonAdvancedSearch.css';

const INITIAL_CRITERIA = {
  name: { value: '', operator: 'contains' },
  supertype: { value: '' },
  subtypes: { values: [], operator: 'OR' },
  types: { values: [], operator: 'OR' },
  hp: { value: '', operator: 'gte' },
  set: { value: '' },
  rarity: { values: [], operator: 'OR' },
  artist: { value: '' },
  evolvesFrom: { value: '' },
  nationalPokedexNumbers: { value: '' },
  legality: { format: '', status: 'Legal' },
  regulationMark: { value: '' }
};

const SUPERTYPES = ['Pokémon', 'Trainer', 'Energy'];

const SUBTYPES = [
  'Basic', 'Stage 1', 'Stage 2', 'VSTAR', 'VMAX', 'V', 'GX', 'EX',
  'TAG TEAM', 'Mega', 'BREAK', 'Prime', 'Legend', 'Restored',
  'Item', 'Supporter', 'Stadium', 'Pokémon Tool',
  'Special', 'Technical Machine', 'Rockets Secret Machine', 'Future', 'Ancient'
];

const TYPES = [
  'Colorless', 'Fire', 'Water', 'Grass', 'Lightning', 
  'Psychic', 'Fighting', 'Darkness', 'Metal', 'Fairy', 'Dragon'
];

const RARITIES = [
  'Common', 'Uncommon', 'Rare', 'Rare Holo', 'Rare Holo V', 'Rare Holo VMAX',
  'Rare Holo VSTAR', 'Rare Holo GX', 'Rare Holo EX', 'Double Rare',
  'Ultra Rare', 'Secret Rare', 'Rare Shiny', 'Rare Shiny GX',
  'Rare ACE', 'Rare BREAK', 'Rare Prime', 'Rare Prism Star',
  'Amazing Rare', 'Radiant Rare', 'Illustration Rare', 
  'Special Illustration Rare', 'Hyper Rare', 'Promo'
];

const FORMATS = ['standard', 'expanded', 'unlimited'];

const REGULATION_MARKS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

function PokemonAdvancedSearch({ onSearch, apiUrl }) {
  const [criteria, setCriteria] = useState(INITIAL_CRITERIA);
  const [sets, setSets] = useState([]);
  const [loadingSets, setLoadingSets] = useState(true);

  // Fetch sets on mount
  useEffect(() => {
    const fetchSets = async () => {
      try {
        const response = await fetch(`${apiUrl}/api/pokemon/sets`);
        const data = await response.json();
        setSets(data.sets || []);
      } catch (error) {
        console.error('Error fetching sets:', error);
      } finally {
        setLoadingSets(false);
      }
    };
    fetchSets();
  }, [apiUrl]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Build criteria object, filtering out empty values
    const searchCriteria = {};
    
    if (criteria.name.value) {
      searchCriteria.name = criteria.name;
    }
    if (criteria.supertype.value) {
      searchCriteria.supertype = criteria.supertype;
    }
    if (criteria.subtypes.values.length > 0) {
      searchCriteria.subtypes = criteria.subtypes;
    }
    if (criteria.types.values.length > 0) {
      searchCriteria.types = criteria.types;
    }
    if (criteria.hp.value) {
      searchCriteria.hp = criteria.hp;
    }
    if (criteria.set.value) {
      searchCriteria.set = criteria.set;
    }
    if (criteria.rarity.values.length > 0) {
      searchCriteria.rarity = criteria.rarity;
    }
    if (criteria.artist.value) {
      searchCriteria.artist = criteria.artist;
    }
    if (criteria.evolvesFrom.value) {
      searchCriteria.evolvesFrom = criteria.evolvesFrom;
    }
    if (criteria.nationalPokedexNumbers.value) {
      searchCriteria.nationalPokedexNumbers = criteria.nationalPokedexNumbers;
    }
    if (criteria.legality.format) {
      searchCriteria.legality = criteria.legality;
    }
    if (criteria.regulationMark.value) {
      searchCriteria.regulationMark = criteria.regulationMark;
    }
    
    onSearch(searchCriteria);
  };

  const handleReset = () => {
    setCriteria(INITIAL_CRITERIA);
  };

  const updateCriteria = (field, updates) => {
    setCriteria(prev => ({
      ...prev,
      [field]: { ...prev[field], ...updates }
    }));
  };

  const toggleArrayValue = (field, value) => {
    setCriteria(prev => {
      const currentValues = prev[field].values;
      const newValues = currentValues.includes(value)
        ? currentValues.filter(v => v !== value)
        : [...currentValues, value];
      return {
        ...prev,
        [field]: { ...prev[field], values: newValues }
      };
    });
  };

  return (
    <form onSubmit={handleSubmit} className="pokemon-advanced-search">
      {/* Name Search */}
      <div className="pokemon-search-section">
        <h3 className="pokemon-section-title">Card Name</h3>
        <div className="pokemon-field-row">
          <input
            type="text"
            value={criteria.name.value}
            onChange={(e) => updateCriteria('name', { value: e.target.value })}
            placeholder="Search by card name..."
            className="pokemon-text-input"
          />
        </div>
      </div>

      {/* Card Type */}
      <div className="pokemon-search-section">
        <h3 className="pokemon-section-title">Card Type</h3>
        <div className="pokemon-field-row">
          <label className="pokemon-field-label">Supertype</label>
          <select
            value={criteria.supertype.value}
            onChange={(e) => updateCriteria('supertype', { value: e.target.value })}
            className="pokemon-select"
          >
            <option value="">Any</option>
            {SUPERTYPES.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>
        
        <div className="pokemon-field-row">
          <label className="pokemon-field-label">Subtypes</label>
          <div className="pokemon-checkbox-group">
            {SUBTYPES.slice(0, 12).map(subtype => (
              <label key={subtype} className="pokemon-checkbox-label">
                <input
                  type="checkbox"
                  checked={criteria.subtypes.values.includes(subtype)}
                  onChange={() => toggleArrayValue('subtypes', subtype)}
                />
                {subtype}
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Energy Types */}
      <div className="pokemon-search-section">
        <h3 className="pokemon-section-title">Energy Type</h3>
        <div className="pokemon-type-buttons">
          {TYPES.map(type => (
            <button
              key={type}
              type="button"
              className={`pokemon-type-btn type-${type.toLowerCase()} ${
                criteria.types.values.includes(type) ? 'selected' : ''
              }`}
              onClick={() => toggleArrayValue('types', type)}
            >
              {type}
            </button>
          ))}
        </div>
      </div>

      {/* HP */}
      <div className="pokemon-search-section">
        <h3 className="pokemon-section-title">HP</h3>
        <div className="pokemon-field-row pokemon-hp-row">
          <select
            value={criteria.hp.operator}
            onChange={(e) => updateCriteria('hp', { operator: e.target.value })}
            className="pokemon-select pokemon-operator-select"
          >
            <option value="eq">=</option>
            <option value="lt">&lt;</option>
            <option value="lte">≤</option>
            <option value="gt">&gt;</option>
            <option value="gte">≥</option>
          </select>
          <input
            type="number"
            value={criteria.hp.value}
            onChange={(e) => updateCriteria('hp', { value: e.target.value })}
            placeholder="e.g., 200"
            className="pokemon-text-input pokemon-hp-input"
            min="0"
            step="10"
          />
        </div>
      </div>

      {/* Set & Rarity */}
      <div className="pokemon-search-section">
        <h3 className="pokemon-section-title">Set & Rarity</h3>
        <div className="pokemon-field-row">
          <label className="pokemon-field-label">Set</label>
          <select
            value={criteria.set.value}
            onChange={(e) => updateCriteria('set', { value: e.target.value })}
            className="pokemon-select"
            disabled={loadingSets}
          >
            <option value="">{loadingSets ? 'Loading sets...' : 'Any Set'}</option>
            {sets.map(set => (
              <option key={set.id} value={set.name}>{set.name} ({set.setCode})</option>
            ))}
          </select>
        </div>

        <div className="pokemon-field-row">
          <label className="pokemon-field-label">Rarity</label>
          <select
            value={criteria.rarity.values[0] || ''}
            onChange={(e) => updateCriteria('rarity', { 
              values: e.target.value ? [e.target.value] : [] 
            })}
            className="pokemon-select"
          >
            <option value="">Any</option>
            {RARITIES.map(rarity => (
              <option key={rarity} value={rarity}>{rarity}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Evolution & Pokedex */}
      <div className="pokemon-search-section">
        <h3 className="pokemon-section-title">Evolution & Pokédex</h3>
        <div className="pokemon-field-row">
          <label className="pokemon-field-label">Evolves From</label>
          <input
            type="text"
            value={criteria.evolvesFrom.value}
            onChange={(e) => updateCriteria('evolvesFrom', { value: e.target.value })}
            placeholder="e.g., Pikachu"
            className="pokemon-text-input"
          />
        </div>
        <div className="pokemon-field-row">
          <label className="pokemon-field-label">National Pokédex #</label>
          <input
            type="number"
            value={criteria.nationalPokedexNumbers.value}
            onChange={(e) => updateCriteria('nationalPokedexNumbers', { value: e.target.value })}
            placeholder="e.g., 25"
            className="pokemon-text-input"
            min="1"
          />
        </div>
      </div>

      {/* Legality & Regulation */}
      <div className="pokemon-search-section">
        <h3 className="pokemon-section-title">Legality</h3>
        <div className="pokemon-field-row">
          <label className="pokemon-field-label">Format</label>
          <select
            value={criteria.legality.format}
            onChange={(e) => updateCriteria('legality', { format: e.target.value })}
            className="pokemon-select"
          >
            <option value="">Any</option>
            {FORMATS.map(format => (
              <option key={format} value={format}>
                {format.charAt(0).toUpperCase() + format.slice(1)}
              </option>
            ))}
          </select>
        </div>
        <div className="pokemon-field-row">
          <label className="pokemon-field-label">Regulation Mark</label>
          <select
            value={criteria.regulationMark.value}
            onChange={(e) => updateCriteria('regulationMark', { value: e.target.value })}
            className="pokemon-select"
          >
            <option value="">Any</option>
            {REGULATION_MARKS.map(mark => (
              <option key={mark} value={mark}>{mark}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Artist */}
      <div className="pokemon-search-section">
        <h3 className="pokemon-section-title">Artist</h3>
        <div className="pokemon-field-row">
          <input
            type="text"
            value={criteria.artist.value}
            onChange={(e) => updateCriteria('artist', { value: e.target.value })}
            placeholder="Search by artist name..."
            className="pokemon-text-input"
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="pokemon-search-actions">
        <button type="button" onClick={handleReset} className="pokemon-reset-btn">
          Reset
        </button>
        <button type="submit" className="pokemon-submit-btn">
          Search
        </button>
      </div>
    </form>
  );
}

export default PokemonAdvancedSearch;
